/**
 * Hộp thoại "Cấu hình AI" — người dùng nhập khoá API của chính mình.
 *
 * Mở bằng sự kiện toàn cục 'open-ai-config' (aiConfig.openAIConfigDialog()) hoặc
 * nút bấm truyền prop. Khoá chỉ lưu trên trình duyệt này, không gửi lên máy chủ để lưu.
 */
import { useEffect, useState, useCallback } from 'react'
import {
  getAIConfig, saveAIConfig, clearAIConfig, aiHeaders,
  type AIConfig, type AIProvider,
} from '../../services/ai/aiConfig'
import { API_CONFIG } from '../../config/api.config'

interface ModelItem { id: string; kind: string; label: string }

const API_BASE = API_CONFIG.python?.apiUrl || '/api'

const PROVIDER_HELP: Record<AIProvider, { name: string; url: string; hint: string }> = {
  openai: {
    name: 'OpenAI',
    url: 'https://platform.openai.com/api-keys',
    hint: 'Dùng cho hỏi đáp, soạn bài, sinh câu hỏi, đọc thành tiếng.',
  },
  gemini: {
    name: 'Google Gemini',
    url: 'https://aistudio.google.com/apikey',
    hint: 'Bắt buộc nếu muốn sinh video bài giảng (Veo là dịch vụ của Google).',
  },
}

export default function AIConfigDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [cfg, setCfg] = useState<AIConfig>(getAIConfig)
  const [tab, setTab] = useState<AIProvider>('openai')
  const [models, setModels] = useState<Record<AIProvider, ModelItem[]>>({ openai: [], gemini: [] })
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null)

  useEffect(() => { if (open) { setCfg(getAIConfig()); setMsg(null) } }, [open])

  /** Lấy danh sách model TỪ NHÀ CUNG CẤP bằng chính khoá vừa nhập. */
  const loadModels = useCallback(async (p: AIProvider) => {
    const key = cfg[p].apiKey.trim()
    if (!key) { setMsg({ type: 'err', text: 'Hãy dán khoá API trước khi lấy danh sách model.' }); return }
    setBusy(true); setMsg(null)
    try {
      const res = await fetch(`${API_BASE}/ai/models`, {
        headers: { 'X-AI-Provider': p, 'X-AI-Key': key, 'X-AI-Model': 'probe' },
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data?.detail || `Lỗi ${res.status}`)
      const list: ModelItem[] = data.models || []
      setModels(m => ({ ...m, [p]: list }))
      // Chưa chọn model thì gợi ý model văn bản đầu tiên; KHÔNG tự đổi lựa chọn đã có.
      setCfg(c => {
        if (c[p].model) return c
        const first = list.find(x => x.kind === 'text')
        return first ? { ...c, [p]: { ...c[p], model: first.id } } : c
      })
      setMsg({ type: 'ok', text: `Khoá hợp lệ — tìm thấy ${list.length} model dùng được.` })
    } catch (e: any) {
      setMsg({ type: 'err', text: e?.message || 'Không lấy được danh sách model.' })
    } finally { setBusy(false) }
  }, [cfg])

  const save = () => {
    const ok = saveAIConfig(cfg)
    setMsg(ok
      ? { type: 'ok', text: 'Đã lưu trên trình duyệt này.' }
      : { type: 'err', text: 'Trình duyệt đang chặn lưu trữ nên CHƯA lưu được. Tắt chế độ ẩn danh hoặc cho phép lưu dữ liệu trang.' })
    if (ok) setTimeout(onClose, 700)
  }

  const clearAll = () => {
    if (!confirm('Xoá khoá API đã lưu trên trình duyệt này?')) return
    clearAIConfig(); setCfg(getAIConfig()); setModels({ openai: [], gemini: [] })
    setMsg({ type: 'ok', text: 'Đã xoá khoá khỏi trình duyệt này.' })
  }

  if (!open) return null

  const p = tab
  const help = PROVIDER_HELP[p]
  const list = models[p]
  const textModels = list.filter(m => m.kind === 'text')
  const ttsModels = list.filter(m => m.kind === 'tts')
  const videoModels = list.filter(m => m.kind === 'video')
  const configured = aiHeaders('text')['X-AI-Key'] ? 'Đã cấu hình' : 'Chưa cấu hình'

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/30 p-4"
         onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-[#fdfcf0] shadow-2xl ring-1 ring-amber-200"
           onClick={e => e.stopPropagation()}>

        <div className="flex items-start justify-between gap-4 border-b border-amber-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Cấu hình AI</h2>
            <p className="mt-1 text-sm text-slate-600">
              Dùng khoá API của chính bạn — chi phí tính trực tiếp với nhà cung cấp,
              hệ thống không trừ điểm.
            </p>
          </div>
          <button onClick={onClose} aria-label="Đóng"
                  className="rounded-lg px-2 py-1 text-2xl leading-none text-slate-400 hover:bg-amber-100 hover:text-slate-700">×</button>
        </div>

        <div className="px-6 pt-4">
          <div className="flex gap-2">
            {(['openai', 'gemini'] as AIProvider[]).map(k => (
              <button key={k} onClick={() => { setTab(k); setMsg(null) }}
                      className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                        tab === k ? 'bg-amber-400 text-slate-900 shadow'
                                  : 'bg-white text-slate-600 ring-1 ring-amber-200 hover:bg-amber-50'}`}>
                {PROVIDER_HELP[k].name}
                {cfg[k].apiKey && <span className="ml-2 text-xs text-teal-700">●</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4 px-6 py-4">
          <p className="text-sm text-slate-600">{help.hint}</p>

          <label className="block">
            <span className="text-sm font-semibold text-slate-700">Khoá API {help.name}</span>
            <input
              type="password" autoComplete="off" spellCheck={false}
              value={cfg[p].apiKey}
              onChange={e => setCfg(c => ({ ...c, [p]: { ...c[p], apiKey: e.target.value } }))}
              placeholder={p === 'openai' ? 'sk-...' : 'Khoá từ Google AI Studio'}
              className="mt-1 w-full rounded-lg border border-amber-300 bg-white px-3 py-2 font-mono text-sm text-slate-800 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
            />
            <a href={help.url} target="_blank" rel="noreferrer"
               className="mt-1 inline-block text-xs font-medium text-teal-700 underline">
              Lấy khoá tại trang {help.name}
            </a>
          </label>

          <div className="flex flex-wrap gap-2">
            <button onClick={() => loadModels(p)} disabled={busy}
                    className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50">
              {busy ? 'Đang kiểm tra…' : 'Kiểm tra khoá & lấy danh sách model'}
            </button>
          </div>

          {msg && (
            <div className={`rounded-lg px-3 py-2 text-sm ${
              msg.type === 'ok' ? 'bg-teal-50 text-teal-800 ring-1 ring-teal-200'
                                : 'bg-orange-50 text-orange-800 ring-1 ring-orange-200'}`}>
              {msg.text}
            </div>
          )}

          {list.length > 0 && (
            <>
              <label className="block">
                <span className="text-sm font-semibold text-slate-700">Model cho văn bản</span>
                <select value={cfg[p].model}
                        onChange={e => setCfg(c => ({ ...c, [p]: { ...c[p], model: e.target.value } }))}
                        className="mt-1 w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm text-slate-800">
                  <option value="">— Chọn model —</option>
                  {textModels.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </label>

              {ttsModels.length > 0 && (
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700">Model đọc thành tiếng (tuỳ chọn)</span>
                  <select value={cfg.voiceModel || ''}
                          onChange={e => setCfg(c => ({ ...c, voiceModel: e.target.value || undefined, voiceProvider: e.target.value ? p : undefined }))}
                          className="mt-1 w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm text-slate-800">
                    <option value="">— Dùng model văn bản —</option>
                    {ttsModels.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                  </select>
                </label>
              )}

              {p === 'gemini' && videoModels.length > 0 && (
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700">Model sinh video</span>
                  <select value={cfg.videoModel || ''}
                          onChange={e => setCfg(c => ({ ...c, videoModel: e.target.value || undefined }))}
                          className="mt-1 w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm text-slate-800">
                    <option value="">— Mặc định —</option>
                    {videoModels.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                  </select>
                </label>
              )}
            </>
          )}

          <label className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 ring-1 ring-amber-200">
            <input type="checkbox" checked={cfg.provider === p}
                   onChange={() => setCfg(c => ({ ...c, provider: p }))}
                   className="h-4 w-4 accent-amber-500" />
            <span className="text-sm text-slate-700">Dùng {help.name} làm nhà cung cấp mặc định</span>
          </label>

          <p className="rounded-lg bg-white px-3 py-2 text-xs leading-relaxed text-slate-500 ring-1 ring-amber-200">
            Khoá chỉ lưu trong trình duyệt này và gửi kèm mỗi lời gọi AI. Không lưu trên máy chủ,
            không nằm trong URL, không xuất ra bài học hay file tải về. Mỗi website phải nhập riêng;
            xoá dữ liệu trang hoặc dùng cửa sổ ẩn danh sẽ mất cấu hình.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-amber-200 px-6 py-4">
          <button onClick={clearAll}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-orange-700 hover:bg-orange-50">
            Xoá khoá trên trình duyệt này
          </button>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500">{configured}</span>
            <button onClick={onClose}
                    className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-amber-100">Huỷ</button>
            <button onClick={save}
                    className="rounded-lg bg-amber-400 px-5 py-2 text-sm font-bold text-slate-900 shadow hover:bg-amber-500">
              Lưu
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Gắn MỘT lần ở App: mở hộp thoại khi có sự kiện 'open-ai-config' từ bất kỳ đâu,
 * kèm một nút nổi luôn hiện để người dùng tự vào nhập/đổi khoá — không phải chờ
 * báo lỗi mới thấy đường vào.
 */
export function AIConfigDialogHost() {
  const [open, setOpen] = useState(false)
  const [hasKey, setHasKey] = useState(false)

  useEffect(() => {
    const onOpen = () => setOpen(true)
    const sync = () => setHasKey(!!aiHeaders('text')['X-AI-Key'])
    sync()
    window.addEventListener('open-ai-config', onOpen)
    window.addEventListener('ai-config-changed', sync)
    return () => {
      window.removeEventListener('open-ai-config', onOpen)
      window.removeEventListener('ai-config-changed', sync)
    }
  }, [])

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          title={hasKey ? 'Khoá AI — đã cấu hình. Bấm để đổi.' : 'Chưa có khoá AI. Bấm để nhập khoá của bạn.'}
          aria-label="Cấu hình AI"
          className="fixed bottom-4 left-4 z-[9998] flex items-center gap-2 rounded-full border border-amber-300 bg-[#fdfcf0]/95 px-3 py-2 text-xs font-bold text-slate-700 shadow-lg backdrop-blur transition hover:bg-amber-100"
        >
          <span aria-hidden>🔑</span>
          <span className="hidden sm:inline">Khoá AI</span>
          <span className={`h-2 w-2 rounded-full ${hasKey ? 'bg-teal-500' : 'bg-orange-400'}`} />
        </button>
      )}
      <AIConfigDialog open={open} onClose={() => setOpen(false)} />
    </>
  )
}
